import React, {
 memo, useState, useCallback, useMemo, useEffect
} from 'react';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import Icon from '@common/components/icon';
import { Button } from '@common/components/button';

interface CalendarBrowserProps {
  currentWeek: Moment;
  onWeekSelect: (week: Moment) => void;
  onClose: () => void;
  isOpen: boolean;
}

const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const CalendarBrowser = memo(({ currentWeek, onWeekSelect, onClose, isOpen }: CalendarBrowserProps) => {
  const { t } = useTranslation();
  const [currentMonth, setCurrentMonth] = useState<Moment>(currentWeek.clone().startOf('month'));

  useEffect(() => {
    if (isOpen) {
      setCurrentMonth(currentWeek.clone().startOf('month'));
    }
  }, [isOpen, currentWeek]);

  const handleMonthChange = useCallback((direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => (direction === 'next'
        ? prev.clone().add(1, 'month')
        : prev.clone().subtract(1, 'month'))
    );
  }, []);

  const handleWeekSelect = useCallback((weekStart: Moment) => {
    onWeekSelect(weekStart);
    onClose();
  }, [onWeekSelect, onClose]);

  const calendarDays = useMemo(() => {
    const monthStart = currentMonth.clone().startOf('month');
    const monthEnd = currentMonth.clone().endOf('month');
    const calendarStart = monthStart.clone().startOf('isoWeek');
    const calendarEnd = monthEnd.clone().endOf('isoWeek');

    const days = [];
    const current = calendarStart.clone();

    while (current.isSameOrBefore(calendarEnd, 'day')) {
      days.push(current.clone());
      current.add(1, 'day');
    }

    return days;
  }, [currentMonth]);

  const weekGroups = useMemo(() => {
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }
    return weeks;
  }, [calendarDays]);

  const isCurrentWeek = useCallback((weekStart: Moment) => {
    return weekStart.isSame(currentWeek, 'isoWeek');
  }, [currentWeek]);

  const isCurrentMonth = useCallback((day: Moment) => {
    return day.isSame(currentMonth, 'month');
  }, [currentMonth]);

  if (!isOpen) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-6 tw-w-[500px] tw-max-h-[80vh] tw-overflow-y-auto">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
          <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">
            {t('calendar:select_week')}
          </h3>
          <Button
            type="default"
            size="small"
            onClick={onClose}
            className="tw-p-1"
          >
            <Icon type="close" className="tw-w-4 tw-h-4" />
          </Button>
        </div>

        {/* Month Navigation */}
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
          <Button
            type="default"
            size="small"
            onClick={() => handleMonthChange('prev')}
            className="tw-p-2"
          >
            <Icon type="chevron_left" className="tw-w-4 tw-h-4" />
          </Button>

          <h4 className="tw-text-base tw-font-medium tw-text-gray-900">
            {currentMonth.format('MMMM YYYY')}
          </h4>

          <Button
            type="default"
            size="small"
            onClick={() => handleMonthChange('next')}
            className="tw-p-2"
          >
            <Icon type="chevron_right" className="tw-w-4 tw-h-4" />
          </Button>
        </div>

        {/* Calendar Grid */}
        <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
          {/* Day Headers */}
          <div className="tw-grid tw-grid-cols-7 tw-bg-gray-50 tw-border-b tw-border-gray-200">
            {weekDays.map((day) => (
              <div key={day} className="tw-text-center tw-text-xs tw-font-medium tw-text-gray-500 tw-py-2 tw-px-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Weeks */}
          <div>
            {weekGroups.map((week) => {
              const weekStart = week[0].clone().startOf('isoWeek');
              const isSelected = isCurrentWeek(weekStart);
              const weekNumber = weekStart.isoWeek();

              return (
                <div key={weekStart.format('GGGG-[W]WW')}>
                  {/* Week number with border line */}
                  <div className="tw-flex tw-items-center tw-py-1 tw-px-2 tw-border-t tw-border-gray-200">
                    <span className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mr-2">
                      W{weekNumber}
                    </span>
                    <div className="tw-flex-1 tw-h-[1px] tw-bg-gray-200" />
                  </div>

                  {/* Week days */}
                  <div
                    className={`tw-grid tw-grid-cols-7 tw-cursor-pointer tw-p-1 ${
                      isSelected
                        ? 'tw-bg-blue-100 tw-border-l-4 tw-border-l-blue-400'
                        : 'hover:tw-bg-gray-50'
                    }`}
                    onClick={() => handleWeekSelect(weekStart)}
                  >
                    {week.map((day) => {
                        const isCurrentMonthDay = !isCurrentMonth(day);
                        const isToday = day.isSame(moment(), 'day');
                        let dayClass = 'tw-text-center tw-text-sm tw-py-2 tw-px-1';
                        if (isCurrentMonthDay) {
                          dayClass += ' tw-text-gray-300';
                        } else if (isToday) {
                          dayClass += ' tw-text-blue-600 tw-font-semibold';
                        } else {
                          dayClass += ' tw-text-gray-700';
                        }
                        return (
                          <div key={day.format('YYYY-MM-DD')} className={dayClass}>
                            {day.format('D')}
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

CalendarBrowser.displayName = 'CalendarBrowser';

export default CalendarBrowser;
